import type { ReactNode } from 'react';

import { legal } from '../../config/legal';

/**
 * The frame the two legal documents render inside.
 *
 * ## Readable, because unreadable terms are not agreed to
 *
 * A narrow measure, real paragraph spacing, and headings somebody can scan. The
 * house style everywhere else on this site is dense and mono; this is the one
 * place where the job is to be read start to finish by somebody who did not
 * want to, so it borrows nothing from it.
 *
 * Headings carry ids so a specific clause can be linked to - which is what
 * anybody quoting a term back at us will want to do.
 */
export function LegalPage({
  title,
  summary,
  children,
}: {
  title: string;
  /** One paragraph, in plain words, before the formal text starts. */
  summary: string;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto max-w-[46rem] px-6 py-14">
      <h1 className="text-4xl font-bold tracking-tight text-ink">{title}</h1>

      <p className="mt-3 font-mono text-[11px] text-faint">
        In effect from {legal.effective}
      </p>

      {/*
        The plain-language summary is not a substitute for the terms and does
        not say it is - it is what somebody reads if they read one thing, and
        writing it forces the rest to be honest.
      */}
      <p className="mt-6 border-l-2 border-line-strong pl-5 text-[15px] leading-relaxed text-text-2">
        {summary}
      </p>

      <div className="mt-10 flex flex-col gap-9">{children}</div>
    </div>
  );
}

/** One numbered clause, addressable by link. */
export function Clause({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-lg font-bold tracking-tight text-ink">
        <a href={`#${id}`} className="hover:underline">
          {title}
        </a>
      </h2>
      <div className="mt-3 flex flex-col gap-3 text-[15px] leading-relaxed text-text-2">
        {children}
      </div>
    </section>
  );
}

/** A list inside a clause, for the things that are genuinely lists. */
export function Points({ children }: { children: ReactNode }) {
  return <ul className="flex list-disc flex-col gap-2 pl-5">{children}</ul>;
}
